import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
  IconButton,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  FormControlLabel,
  Alert,
  Divider,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoLibrary as PhotoIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  Source as SourceIcon,
  LocationOn as LocationIcon,
  Event as EventIcon
} from '@mui/icons-material';
import {
  LifeEvent,
  Location,
  Photo,
  Relationship
} from '../../types';
import { LifeEventType } from '../../types/person';
import { LifeEventFormData } from '../../services/timelineService';

// Define RelationshipSummary interface
export interface RelationshipSummary {
  id: string;
  type: string;
  relatedPersonName: string;
  relatedPersonId: string;
  startDate?: string;
  endDate?: string;
}

// Props interface for the EventForm component
export interface EventFormProps {
  personId: string;
  event?: LifeEvent; // Existing event for editing, undefined for creation
  open: boolean;
  onClose: () => void;
  onSubmit: (eventData: LifeEventFormData, eventId?: string) => Promise<LifeEvent | void>;
  // Optional: For linking photos/relationships directly from the form
  availablePhotos?: Photo[];
  availableRelationships?: RelationshipSummary[];
  onLinkPhotoToEvent?: (eventId: string, photoId: string) => Promise<LifeEvent | void>;
  onLinkRelationshipToEvent?: (eventId: string, relationshipId: string) => Promise<LifeEvent | void>;
}

// Event type templates for pre-filling fields
const EVENT_TEMPLATES: Record<LifeEventType, Partial<LifeEventFormData>> = {
  birth: {
    description: 'Birth',
    sources: []
  },
  death: {
    description: 'Death',
    sources: []
  },
  marriage: {
    description: 'Marriage ceremony',
    sources: []
  },
  divorce: {
    description: 'Divorce finalized',
    sources: []
  },
  education: {
    description: 'Graduated from',
    sources: []
  },
  employment: {
    description: 'Started working at',
    sources: []
  },
  military: {
    description: 'Military service',
    sources: []
  },
  immigration: {
    description: 'Immigrated to',
    sources: []
  },
  residence: {
    description: 'Moved to',
    sources: []
  },
  achievement: {
    description: 'Achievement',
    sources: []
  },
  other: {
    description: '',
    sources: []
  }
};

// Validation interface
interface FormValidation {
  isValid: boolean;
  errors: Record<string, string>;
}

const EventForm: React.FC<EventFormProps> = ({
  personId,
  event,
  open,
  onClose,
  onSubmit,
  availablePhotos = [],
  availableRelationships = [],
  onLinkPhotoToEvent,
  onLinkRelationshipToEvent
}) => {
  // Form state
  const [formData, setFormData] = useState<LifeEventFormData>({
    personId,
    type: 'other',
    date: '',
    location: undefined,
    description: '',
    sources: [],
    photoIds: [],
    relationshipIds: []
  });

  // UI state
  const [validation, setValidation] = useState<FormValidation>({ isValid: true, errors: {} });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newSource, setNewSource] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([]);
  const [selectedRelationships, setSelectedRelationships] = useState<string[]>([]);
  const [locationInput, setLocationInput] = useState({
    city: '',
    state: '',
    country: ''
  });

  // Initialize form when editing existing event or when event changes
  useEffect(() => {
    if (event) {
      setFormData({
        personId: event.personId,
        type: event.type,
        date: event.date || '',
        location: event.location,
        description: event.description,
        sources: event.sources || [],
        photoIds: [], // Will be populated from existing event if needed
        relationshipIds: [] // Will be populated from existing event if needed
      });

      // Set location inputs
      if (event.location) {
        setLocationInput({
          city: event.location.city || '',
          state: event.location.state || '',
          country: event.location.country || ''
        });
      }
    } else {
      // Reset form for new event
      setFormData({
        personId,
        type: 'other',
        date: '',
        location: undefined,
        description: '',
        sources: [],
        photoIds: [],
        relationshipIds: []
      });
      setLocationInput({ city: '', state: '', country: '' });
    }

    setSelectedPhotos([]);
    setSelectedRelationships([]);
    setValidation({ isValid: true, errors: {} });
  }, [event, personId, open]);

  // Handle event type change and apply template
  const handleEventTypeChange = (type: LifeEventType) => {
    const template = EVENT_TEMPLATES[type];
    setFormData(prev => ({
      ...prev,
      type,
      description: template.description || prev.description,
      sources: template.sources || prev.sources
    }));
  };

  // Handle location changes
  const handleLocationChange = (field: keyof typeof locationInput, value: string) => {
    const newLocationInput = { ...locationInput, [field]: value };
    setLocationInput(newLocationInput);

    // Update form data location
    const hasLocation = newLocationInput.city || newLocationInput.state || newLocationInput.country;
    setFormData(prev => ({
      ...prev,
      location: hasLocation ? {
        city: newLocationInput.city || undefined,
        state: newLocationInput.state || undefined,
        country: newLocationInput.country || undefined
      } : undefined
    }));
  };

  // Add source
  const handleAddSource = () => {
    if (newSource.trim()) {
      setFormData(prev => ({
        ...prev,
        sources: [...(prev.sources || []), newSource.trim()]
      }));
      setNewSource('');
    }
  };

  // Remove source
  const handleRemoveSource = (index: number) => {
    setFormData(prev => ({
      ...prev,
      sources: prev.sources?.filter((_, i) => i !== index) || []
    }));
  };

  // Photo selection handlers
  const handlePhotoToggle = (photoId: string) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  // Relationship selection handlers
  const handleRelationshipToggle = (relationshipId: string) => {
    setSelectedRelationships(prev => 
      prev.includes(relationshipId) 
        ? prev.filter(id => id !== relationshipId)
        : [...prev, relationshipId]
    );
  };

  // Form validation
  const validateForm = (): FormValidation => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!formData.type) {
      errors.type = 'Event type is required';
    }
    if (!formData.description?.trim()) {
      errors.description = 'Event description is required';
    }
    if (!formData.date) {
      errors.date = 'Event date is required';
    }

    // Date validation
    if (formData.date) {
      const eventDate = new Date(formData.date);
      const now = new Date();
      
      // Basic date validation
      if (isNaN(eventDate.getTime())) {
        errors.date = 'Invalid date format';
      } else if (eventDate > now && !['birth', 'death'].includes(formData.type)) {
        // Allow future dates for most events, but warn
        // Could add more sophisticated validation here
      }
    }

    // Check for potential duplicates (basic check)
    // In a real implementation, this would query existing events
    
    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  // Handle form submission
  const handleSubmit = async () => {
    const validationResult = validateForm();
    setValidation(validationResult);

    if (!validationResult.isValid) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare form data with selected photos and relationships
      const submitData: LifeEventFormData = {
        ...formData,
        photoIds: selectedPhotos,
        relationshipIds: selectedRelationships
      };

      // Submit the event
      const result = await onSubmit(submitData, event?.id);
      
      // If successful and we have linking functions, handle photo/relationship linking
      if (result && result.id) {
        // Link photos if onLinkPhotoToEvent is provided
        if (onLinkPhotoToEvent && selectedPhotos.length > 0) {
          for (const photoId of selectedPhotos) {
            try {
              await onLinkPhotoToEvent(result.id, photoId);
            } catch (error) {
              console.warn(`Failed to link photo ${photoId} to event ${result.id}:`, error);
            }
          }
        }

        // Link relationships if onLinkRelationshipToEvent is provided
        if (onLinkRelationshipToEvent && selectedRelationships.length > 0) {
          for (const relationshipId of selectedRelationships) {
            try {
              await onLinkRelationshipToEvent(result.id, relationshipId);
            } catch (error) {
              console.warn(`Failed to link relationship ${relationshipId} to event ${result.id}:`, error);
            }
          }
        }
      }

      onClose();
    } catch (error) {
      console.error('Error submitting event:', error);
      setValidation({
        isValid: false,
        errors: { submit: 'Failed to save event. Please try again.' }
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EventIcon />
          {event ? 'Edit Life Event' : 'Add New Life Event'}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box component="form" noValidate>
            {/* General validation error */}
            {validation.errors.submit && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {validation.errors.submit}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Event Type Selection */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!validation.errors.type}>
                  <InputLabel>Event Type</InputLabel>
                  <Select
                    value={formData.type}
                    label="Event Type"
                    onChange={(e) => handleEventTypeChange(e.target.value as LifeEventType)}
                  >
                    <MenuItem value="birth">Birth</MenuItem>
                    <MenuItem value="death">Death</MenuItem>
                    <MenuItem value="marriage">Marriage</MenuItem>
                    <MenuItem value="divorce">Divorce</MenuItem>
                    <MenuItem value="education">Education</MenuItem>
                    <MenuItem value="employment">Employment</MenuItem>
                    <MenuItem value="military">Military Service</MenuItem>
                    <MenuItem value="immigration">Immigration</MenuItem>
                    <MenuItem value="residence">Change of Residence</MenuItem>
                    <MenuItem value="achievement">Achievement</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                  {validation.errors.type && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {validation.errors.type}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Event Date */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Event Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  error={!!validation.errors.date}
                  helperText={validation.errors.date || 'Select the date when this event occurred'}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  error={!!validation.errors.description}
                  helperText={validation.errors.description || 'Brief description of the event'}
                  multiline
                  rows={2}
                />
              </Grid>

              {/* Location Section */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LocationIcon />
                      <Typography>Location Details</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="City"
                          value={locationInput.city}
                          onChange={(e) => handleLocationChange('city', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="State/Province"
                          value={locationInput.state}
                          onChange={(e) => handleLocationChange('state', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Country"
                          value={locationInput.country}
                          onChange={(e) => handleLocationChange('country', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Sources Section */}
              <Grid item xs={12}>
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SourceIcon />
                      <Typography>Source Documentation</Typography>
                      {formData.sources && formData.sources.length > 0 && (
                        <Chip 
                          size="small" 
                          label={formData.sources.length} 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {/* Add new source */}
                      <Box display="flex" gap={1} mb={2}>
                        <TextField
                          fullWidth
                          label="Add Source"
                          value={newSource}
                          onChange={(e) => setNewSource(e.target.value)}
                          placeholder="Enter source reference (document, record, etc.)"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddSource();
                            }
                          }}
                        />
                        <IconButton
                          onClick={handleAddSource}
                          disabled={!newSource.trim()}
                          color="primary"
                        >
                          <AddIcon />
                        </IconButton>
                      </Box>

                      {/* Existing sources */}
                      {formData.sources && formData.sources.length > 0 && (
                        <List dense>
                          {formData.sources.map((source, index) => (
                            <ListItem key={index} divider>
                              <ListItemText primary={source} />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  onClick={() => handleRemoveSource(index)}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Grid>

              {/* Photo Linking Section */}
              {availablePhotos.length > 0 && (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PhotoIcon />
                        <Typography>Link Photos</Typography>
                        {selectedPhotos.length > 0 && (
                          <Chip 
                            size="small" 
                            label={selectedPhotos.length} 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                        <Grid container spacing={1}>
                          {availablePhotos.map((photo) => (
                            <Grid item xs={6} sm={4} md={3} key={photo.id}>
                              <Paper
                                sx={{
                                  p: 1,
                                  border: selectedPhotos.includes(photo.id) 
                                    ? '2px solid' 
                                    : '1px solid',
                                  borderColor: selectedPhotos.includes(photo.id) 
                                    ? 'primary.main' 
                                    : 'divider',
                                  cursor: 'pointer'
                                }}
                                onClick={() => handlePhotoToggle(photo.id)}
                              >
                                <Box
                                  component="img"
                                  src={photo.thumbnailURL || photo.storageURL}
                                  alt={photo.caption || photo.fileName}
                                  sx={{
                                    width: '100%',
                                    height: 80,
                                    objectFit: 'cover',
                                    borderRadius: 1
                                  }}
                                />
                                <Typography variant="caption" display="block" noWrap>
                                  {photo.caption || photo.fileName}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={selectedPhotos.includes(photo.id)}
                                      onChange={() => handlePhotoToggle(photo.id)}
                                      size="small"
                                    />
                                  }
                                  label=""
                                  sx={{ m: 0 }}
                                />
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}

              {/* Relationship Linking Section */}
              {availableRelationships.length > 0 && (
                <Grid item xs={12}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <PeopleIcon />
                        <Typography>Link Relationships</Typography>
                        {selectedRelationships.length > 0 && (
                          <Chip 
                            size="small" 
                            label={selectedRelationships.length} 
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {availableRelationships.map((relationship) => (
                          <ListItem key={relationship.id} divider>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={selectedRelationships.includes(relationship.id)}
                                  onChange={() => handleRelationshipToggle(relationship.id)}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2">
                                    {relationship.type} - {relationship.relatedPersonName}
                                  </Typography>
                                  {relationship.startDate && (
                                    <Typography variant="caption" color="text.secondary">
                                      {relationship.startDate}
                                      {relationship.endDate && ` - ${relationship.endDate}`}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !validation.isValid}
        >
          {isSubmitting ? 'Saving...' : (event ? 'Update Event' : 'Create Event')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventForm;